import React, {useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import axios from 'axios';
import {AutoComplete} from 'primereact/autocomplete';
import {Button} from 'primereact/button';
import {Calendar} from 'primereact/calendar';
import {Dropdown} from 'primereact/dropdown';
import {InputTextarea} from 'primereact/inputtextarea';
import {ProgressBar} from 'primereact/progressbar';
import CrmTimelineComponent from '../CrmTimelineComponent';

const TaskFormComponent = props => {
  const {t} = props;
  const history = useHistory();
  const {entity, id} = useParams();
  const taskId = props.taskId || window.App.urlVars().taskId || id;
  const editing = Boolean(props.taskId || (id && entity === 'task') || window.App.urlVars().update === 'task');
  const headers = useMemo(() => ({Authorization: 'Bearer ' + localStorage.getItem('userToken'), 'App-Network': localStorage.getItem('networkId')}), []);
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnerQuery, setPartnerQuery] = useState(props.partnerName || '');
  const [comment, setComment] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [timelineRefreshToken, setTimelineRefreshToken] = useState(0);
  const [formData, setFormData] = useState({
    deadline: new Date(), reminder: null, partner: props.partnerId || null,
    deal: props.dealId || null, lead: props.leadId || null, status: null, note: ''
  });

  useEffect(() => {
    const requests = [
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/crm-subject?item_per_page=200', {headers}),
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/employee?item_per_page=200&sort=name&direction=asc', {headers}),
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/crm-task-status?item_per_page=200&sort=position&direction=asc', {headers})
    ];
    if (editing) requests.push(axios.get(process.env.REACT_APP_API_HOST + '/api/v2/crm-task/' + taskId, {headers}));
    setLoading(true);
    Promise.all(requests).then(responses => {
      setSubjects(responses[0].data.data.items || []);
      setEmployees(responses[1].data.data.items || []);
      const availableStatuses = (responses[2].data.data.items || []).filter(item => item.active);
      setStatuses(availableStatuses);
      if (responses[3]) {
        const task = responses[3].data.data;
        setFormData({...task,
          status: task.status?.id || task.status || null,
          subject: task.subject?.id || task.subject || null,
          employee: task.employee?.id || task.employee || null,
          partner: task.partner?.id || task.partner || null,
          deal: task.deal?.id || task.deal || null,
          lead: task.lead?.id || task.lead || null,
          deadline: task.deadline ? new Date(task.deadline) : null,
          reminder: task.reminder ? new Date(task.reminder) : null
        });
        setPartnerQuery(task.partner?.name || '');
      } else {
        setFormData(current => ({...current, status: availableStatuses.find(item => item.default)?.id || availableStatuses[0]?.id || null}));
      }
    }).catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  }, [editing, headers, taskId, t]);

  const set = (field, value) => setFormData(current => ({...current, [field]: value}));
  const searchPartner = event => {
    const query = String(event.query || '').trim();
    if (query.length < 2) return setPartners([]);
    axios.get(process.env.REACT_APP_API_HOST + '/api/v2/partner', {headers, params: {item_per_page: 20, 'search[name][operator]': 'like', 'search[name][value]': `*${query}*`}})
      .then(response => setPartners((response.data.data.items || []).map(item => ({...item, displayName: item.name}))));
  };
  const serializeDate = value => value instanceof Date && !Number.isNaN(value.getTime()) ? value.toISOString() : null;
  const save = goBack => {
    if (!formData.subject || !formData.employee || !formData.status) return window.App.toastShow('error', t('error'), t('fieldRequired'));
    setLoading(true);
    const payload = {...formData,
      subject: formData.subject, employee: formData.employee, status: formData.status,
      partner: formData.partner || null, deadline: serializeDate(formData.deadline), reminder: serializeDate(formData.reminder),
      comments: undefined, successfulAt: undefined, successful_at: undefined
    };
    const request = editing
      ? axios.patch(process.env.REACT_APP_API_HOST + '/api/v2/crm-task/' + taskId, payload, {headers})
      : axios.post(process.env.REACT_APP_API_HOST + '/api/v2/crm-task', payload, {headers});
    request.then(response => {
      window.App.toastShow('success', t('toastSaveSuccess'));
      if (props.embedded) props.onSaved?.(response.data.data);
      else history.push(goBack ? (props.backlink || '/crm/task') : '/crm/task/update/' + response.data.data.id);
    }).catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response)).finally(() => setLoading(false));
  };
  const addComment = () => {
    if (!comment.trim()) return;
    setCommentSaving(true);
    axios.post(process.env.REACT_APP_API_HOST + '/api/v2/crm-task/' + taskId + '/comments', {message: comment.trim()}, {headers})
      .then(() => {setComment(''); setTimelineRefreshToken(value => value + 1);})
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response)).finally(() => setCommentSaving(false));
  };

  return <div className="task-form widget">
    {loading && <ProgressBar mode="indeterminate" style={{height: 5, position: 'absolute', top: 0, left: 0, borderRadius: 0}}/>}
    <div className="p-grid">
      <div className="p-col-12 p-lg-4"><div className="p-field p-fluid"><label>{t('crmTaskStatus')}</label><Dropdown value={formData.status} options={statuses} optionLabel="name" optionValue="id" onChange={event => set('status', event.value)}/></div></div>
      <div className="p-col-12 p-lg-4"><div className="p-field p-fluid"><label>{t('fieldSubject')}</label><Dropdown value={formData.subject} options={subjects} optionLabel="name" optionValue="id" filter onChange={event => set('subject', event.value)}/></div></div>
      <div className="p-col-12 p-lg-4"><div className="p-field p-fluid"><label>{t('fieldEmployee')}</label><Dropdown value={formData.employee} options={employees} optionLabel="name" optionValue="id" filter onChange={event => set('employee', event.value)}/></div></div>
      <div className="p-col-12 p-lg-4"><div className="p-field p-fluid"><label>{t('deadline')}</label><Calendar showIcon showButtonBar dateFormat="yy-mm-dd" value={formData.deadline} onChange={event => set('deadline', event.value)}/></div></div>
      <div className="p-col-12 p-lg-4"><div className="p-field p-fluid"><label>{t('fieldReminder')}</label><Calendar showIcon showButtonBar dateFormat="yy-mm-dd" value={formData.reminder} onChange={event => set('reminder', event.value)}/></div></div>
      <div className="p-col-12 p-lg-4"><div className="p-field p-fluid"><label>{t('fieldPartner')}</label><AutoComplete value={partnerQuery} suggestions={partners} completeMethod={searchPartner} field="displayName" forceSelection onChange={event => {setPartnerQuery(event.value); if (!event.value) set('partner', null);}} onSelect={event => {setPartnerQuery(event.value.name); set('partner', event.value.id);}}/></div></div>
      <div className="p-col-12"><div className="p-field p-fluid"><label>{t('fieldNote')}</label><InputTextarea rows={3} value={formData.note || ''} onChange={event => set('note', event.target.value)}/></div></div>
    </div>
    <div className="p-d-flex p-jc-end"><Button type="button" label={t('save')} icon="pi pi-save" loading={loading} className="p-button-success" onClick={() => save(false)}/>{!props.embedded && <Button type="button" label={t('saveAndBack')} icon="pi pi-arrow-left" loading={loading} className="p-button-success p-ml-2" onClick={() => save(true)}/>}</div>
    {editing && <div className="p-mt-4"><h3>{t('crmCommentsAndHistory')}</h3><div className="p-inputgroup p-mb-3"><InputTextarea rows={2} value={comment} placeholder={t('crmAddCommentPlaceholder')} onChange={event => setComment(event.target.value)}/><Button type="button" icon="pi pi-send" label={t('crmAddComment')} loading={commentSaving} disabled={!comment.trim()} onClick={addComment}/></div><CrmTimelineComponent taskId={taskId} refreshToken={timelineRefreshToken}/></div>}
  </div>;
};

export default withTranslation('common')(TaskFormComponent);
